import { Add, Edit } from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import useAuthToken from '../../../hooks/getUserData';
import EditPicture from './EditProductPicture';
import UploadPicture from './UploadProductPicture';
import { updateProductImages } from '../../../api/products';

const cardStyle = {
  marginBottom: '16px',
  height: '488px',
};

const ProductPicture = ({
  product,
  fetchProduct,
  setMessage,
  setSnackbarOpen,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const token = useAuthToken();

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [product]);

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  const handleUploadClick = () => {
    setIsUploadDialogOpen(true);
  };

  const handleEditClick = () => {
    setIsEditDialogOpen(true);
  };

  const handleUploadClose = () => {
    setError(null);
    setIsUploadDialogOpen(false);
  };

  const handleEditClose = () => {
    setError(null);
    setIsEditDialogOpen(false);
  };

  const handleUploadSubmit = async (selectedFiles) => {
    setError(null);
    setLoading(true);
    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append('image', file);
    });
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/v2/products/images/${product.id}`,
        {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: token,
          },
        }
      );
      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error?.message || errorResponse.message);
      }
      setLoading(false);
      handleUploadClose();
      await fetchProduct();
      setMessage('Product updated successfully');
      setSnackbarOpen(true);
    } catch (error) {
      setLoading(false)
      setError(error.message || 'Failed to upload image. Please try again.');
    }
  };

  const handleEditSubmit = async (pictures) => {
    setLoading(true);
    setError(null);
    try {
      const response = await updateProductImages(pictures, product.id, token);
      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error?.message || errorResponse.message);
      }
      setLoading(false);
      handleEditClose();
      await fetchProduct();
      setMessage('Product images updated successfully');
      setSnackbarOpen(true);
    } catch (error) {
      setLoading(false);
      setError('Failed to update images. Please try again');
    } 
  };

  return (
    <Card style={cardStyle}>
      <CardHeader
        title="Product images"
        action={
          <>
            <IconButton title="Add" onClick={handleUploadClick}>
              <Add />
            </IconButton>
            <IconButton title="Edit" onClick={handleEditClick}>
              <Edit />
            </IconButton>
          </>
        }
        sx={{ paddingBottom: 0 }}
      />
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}>
        <>
          {product.pictures && product?.pictures?.length > 0 ? (
            <>
              <div style={{ flex: 1, maxHeight: '400px', overflowY: 'auto' }}>
                {product.pictures &&
                  product.pictures.map((image, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: '12px',
                        display: 'flex',
                        justifyContent: 'center',
                      }}>
                      <Button
                        onClick={() => handleImageClick(index)}
                        sx={{
                          background: 'none',
                          padding: 0,
                          border:
                            selectedImageIndex === index
                              ? '2px solid lightblue'
                              : '2px solid transparent',
                          cursor: 'pointer',
                          transition: 'border 0.2s ease',
                        }}
                        disableRipple>
                        <img
                          src={`${image}?w=80&h=80&fit=crop&auto=format`}
                          srcSet={`${image}?w=80&h=80&fit=crop&auto=format&dpr=2 2x`}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'cover',
                          }}
                          alt={product.title}
                        />
                      </Button>
                    </div>
                  ))}
              </div>
              <div
                style={{
                  flex: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '2px solid #dde6e9',
                  width: '300px',
                  height: '400px',
                }}>
                {product.pictures && product.pictures?.length > 0 && (
                  <img
                    src={`${product.pictures[selectedImageIndex]}?w=300&h=300&fit=crop&auto=format`}
                    srcSet={`${product.pictures[selectedImageIndex]}?w=300&h=300&fit=crop&auto=format&dpr=2 2x`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'cover',
                    }}
                    alt={product.title}
                  />
                )}
              </div>
            </>
          ) : (
            <Typography
              sx={{ textAlign: 'center' }}
              variant="caption"
              component="p">
              You can add product images here.
            </Typography>
          )}
        </>
      </CardContent>
      <EditPicture
        loading={loading}
        open={isEditDialogOpen}
        handleClose={handleEditClose}
        onSubmit={handleEditSubmit}
        error={error}
        width={product.pictures?.length > 3 ? 'md' : 'sm'}
        productPictures={product?.pictures}
      />
      <UploadPicture
        loading={loading}
        open={isUploadDialogOpen}
        handleClose={handleUploadClose}
        onSubmit={handleUploadSubmit}
        error={error}
        productPictures={product.pictures}
      />
    </Card>
  );
};

export default ProductPicture;
